import React, { useEffect, useMemo } from 'react'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { ConfirmationModal, Table } from 'components/Ui'
import { updateApiRecord, testConnection } from 'services/MachshipIntegrationService'
import { Success, Error } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { activeStatus } from 'components/helper'
import { Button } from 'components/Form'
import { HiOutlineCheckCircle } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'

injectReducer('machshipIntegration', reducer)

export default function MachshipIntegration() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(state => state.machshipIntegration.data)
    const { company, customer } = useSelector(state => state.filterData)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        company,
        customer,
    ])

    const fetchData = () => dispatch(getData({ ...tableData, company: company, customer: customer }))

    const handleStatus = (id, data) => {
        updateApiRecord(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch(error => {
                console.log('Error: ', error)
                Error('Failed to update status!')
            })
    }

    const handleTestConnection = id => {
        testConnection(id)
            .then(response => {
                if (response.data.status === 'success') {
                    Success('Connection test successful!')
                } else {
                    Error(response.data.message || 'Connection test failed!')
                }
            })
            .catch(error => {
                console.log('Error: ', error)
                Error('Connection test failed!')
            })
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => row?.company?.name || row?.company?.code || 'N/A',
            },
            {
                field: 'customer',
                header: 'Customer',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => row?.customer?.customer_code || 'N/A',
            },
            {
                field: 'api_url',
                header: 'API URL',
                className: 'min-w-[200px]',
                hide: false,
            },
            {
                field: 'is_test_mode',
                header: 'Test Mode',
                className: 'min-w-[100px]',
                hide: false,
                renderCell: row => (
                    <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            row.is_test_mode
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}
                    >
                        {row.is_test_mode ? 'Test' : 'Live'}
                    </span>
                ),
            },
            {
                header: 'Status',
                field: 'is_active',
                hide: false,
                renderCell: row => {
                    return checkPermission('machship-integration', 'status') ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                handleStatus(row.id, { is_active: !row.is_active })
                            }}
                        >
                            {activeStatus(row.is_active)}
                        </ConfirmationModal>
                    ) : (
                        activeStatus(row.is_active)
                    )
                },
            },
            {
                header: 'Actions',
                field: 'actions',
                className: 'min-w-[120px]',
                hide: false,
                renderCell: row => {
                    return (
                        <div className="flex items-center gap-2">
                            <Tooltip title="Test Connection">
                                <Button
                                    size="sm"
                                    variant="twoTone"
                                    color="blue-600"
                                    onClick={() => handleTestConnection(row.id)}
                                    className="!px-2 !py-1"
                                >
                                    <HiOutlineCheckCircle size={16} />
                                </Button>
                            </Tooltip>
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            company,
            customer,
        ]
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Machship Integration</h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('machship-integration', 'create') ? '/company-management/machship-integration/create' : ''
                }
                pagingData={tableData}
                onChange={e => dispatch(setTableData(e))}
            />
        </>
    )
}
